/**
 * §7.6 Business ROI — consolidation story.  Left: 8 disparate systems
 * you'd otherwise need.  Right: Kalki Ai Cloud unified platform.
 */

import React from 'react'
import {
  UsersRound, LineChart, GraduationCap, CreditCard, PieChart, Bot,
  ContactRound, Cog, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { ROI_SYSTEMS } from '../data'
import { TONE } from '../_shared'

const ICONS = { UsersRound, LineChart, GraduationCap, CreditCard, PieChart, Bot, ContactRound, Cog }

export default function ROI() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow tone="green">Business ROI</Eyebrow>
          <SectionHeading align="center"
            title={<>Consolidate eight tools{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">into one platform.</span></>}
            subtitle="Without Kalki Ai Cloud, businesses typically need to procure, integrate and maintain multiple point tools. Kalki Ai Cloud combines them into a unified platform — reducing operational complexity and accelerating time to market."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          {/* LEFT — 8 systems */}
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.02] p-5 md:p-6 backdrop-blur-xl overflow-hidden">
            <div className="pointer-events-none absolute inset-0 opacity-40"
              style={{ background: 'radial-gradient(80% 40% at 20% 0%, rgba(244,63,94,0.10), transparent 70%)' }} />
            <div className="relative flex items-center justify-between mb-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-rose-400">Without Kalki Ai Cloud</div>
              <div className="text-[10.5px] text-zinc-500">8 vendors · 8 contracts</div>
            </div>
            <div className="relative grid grid-cols-2 gap-2.5">
              {ROI_SYSTEMS.map((s) => {
                const tone = TONE[s.tone] || TONE.orange
                const Icon = ICONS[s.iconKey] || Sparkles
                return (
                  <div key={s.id} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 14, height: 14 }} />
                    </span>
                    <span className="text-[13px] font-medium text-zinc-200">{s.label}</span>
                  </div>
                )
              })}
            </div>
            <div className="relative mt-4 rounded-lg border border-rose-400/25 bg-rose-400/[0.06] px-3 py-2 text-[12.5px] text-rose-200 leading-relaxed">
              Eight procurement cycles. Eight integrations. Eight vendor SLAs. Ongoing maintenance and coordination overhead.
            </div>
          </div>

          {/* RIGHT — Kalki Ai Cloud unified */}
          <div className="relative rounded-3xl border border-brand-gold/40 bg-gradient-to-br from-brand-gold/[0.08] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden">
            <div className="pointer-events-none absolute inset-0 opacity-70"
              style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.14), transparent 70%)' }} />
            <div className="relative flex items-center justify-between mb-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-brand-gold">With Kalki Ai Cloud</div>
              <div className="text-[10.5px] text-emerald-400 font-mono">1 licence · 1 platform</div>
            </div>

            <div className="relative flex items-center justify-center rounded-2xl border border-white/10 bg-black/40 p-6 md:p-8">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-brand-gold/40 bg-brand-gold/15">
                  <Sparkles className="h-6 w-6 text-brand-gold" />
                </div>
                <div className="text-lg font-semibold text-white">Kalki Ai Cloud Platform</div>
                <div className="text-[12.5px] text-zinc-400 max-w-xs">
                  AI · Trading · CRM · Academy · Billing · Analytics · Notifications · White-Label admin — fully integrated.
                </div>
              </div>
            </div>

            <div className="relative mt-4 grid grid-cols-3 gap-2">
              {[
                { l: 'One contract',   v: '✓' },
                { l: 'One integration', v: '✓' },
                { l: 'One vendor SLA',  v: '✓' },
              ].map((k) => (
                <div key={k.l} className="rounded-lg border border-emerald-400/25 bg-emerald-400/[0.06] px-2 py-1.5">
                  <div className="text-[9.5px] font-mono uppercase tracking-wider text-emerald-400">{k.l}</div>
                  <div className="text-[15px] font-semibold text-white">{k.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4">
          <p className="text-[13px] text-zinc-400 leading-relaxed">
            <span className="font-semibold text-zinc-200">Note:</span>{' '}
            The Kalki Ai Cloud platform reduces operational complexity and accelerates time to market. Actual financial outcomes depend on your specific business model, market conditions and execution.
          </p>
        </div>
      </Container>
    </section>
  )
}
